import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ReportsCheckboxes,
  Checkbox,
  CHECKBOX_INFO,
  ApiTypeKeys,
  ReportsAvailability,
  ReportsOptions
} from './reports.models';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../reducers';
import { isEqual, sortBy } from 'lodash';
import { Subscription, Observable, combineLatest, BehaviorSubject } from 'rxjs';
import {
  selectReportsOptions,
  selectReportsDate,
  selectReportsYearFilter,
  selectReportsMonthFilter,
  selectReportsYears,
  selectReportsMonths,
  selectReportsAvailability,
  selectReportsVisited,
  selectReportsDatesMax
} from './reports.selectors';
import {
  SetReportsCheckboxOptionsAction,
  ReportsDownloadAction,
  SetReportsYearFilterAction,
  SetReportsMonthFilterAction,
  LoadReportsAvailabilityAction,
  SetReportsVisitAction,
  LoadReportsDatesAction,
  ResetReportsDataAction,
  ReportsExcelDownloadAction
} from './reports.actions';
import { map, take, filter, tap, withLatestFrom, distinctUntilChanged } from 'rxjs/operators';
import {
  selectCountrySelected, selectMainFilterSelectedAccount, selectMainFilterSelectedClient,
  selectMainPageNotes
} from '../../shared/layout/main.selectors';
// import { AddErrorToastAction, RemoveToastsAction } from '../toaster/toaster.actions';
import { Month } from '../../global/dates';
import { formatDateFromAndTo } from '../../global/utilities.service';
import { selectAuthUserReports } from '../auth/auth.selectors';
import * as moment from 'moment';
import { isNullOrUndefined } from 'util';
import { DateTime } from 'luxon';
import { Actions, FieldSize } from 'atlas-cdk';
import { ToastService } from '../../shared/toaster_/toaster_.service';
import { AlertService } from 'atlas-alert';
@Component({
  standalone: false,
  selector: 'dsp-reports',
  templateUrl: 'reports.container.html',
  styleUrls: ['reports.container.scss']
})
export class ReportsContainerComponent implements OnInit, OnDestroy {
  public sizeMedium = FieldSize.medium;
  public year: number;
  public month: number;

  public allCheckbox: { value: boolean; tooltip?: string } = { value: false };
  public checkboxes: ReportsCheckboxes;
  public showCheckboxes = false;
  public years$: Observable<number[]>;
  public yearFilterOptions$: Observable<{ label: number; value: number }[]>;
  public months$: Observable<Month[]>;
  public monthFilterOptions$: Observable<{ label: string; value: number }[]>;
  public checkboxes$: Observable<Checkbox[]>;
  public disableAll$: Observable<boolean>;
  public newDate: any;
  public todate = DateTime.utc().toISO();
  public from = DateTime.utc().minus({ months: 1 }).startOf('day').toISO();
  public to = DateTime.utc().toISO();

  public reportTypes = [];
  public reportTypeOptions: { label: string; value: string }[] = [];
  public reportType: { name: string, value: string };
  public showExcel = false;
  public country = '';


  private readonly subscription: Subscription = new Subscription();
  public constructor(
    private readonly store: Store<AppState>,
    private readonly _toasterService: ToastService,
    private readonly alertService: AlertService
  ) {
    this.years$ = this.selectYears();
    this.yearFilterOptions$ = this.years$.pipe(
      map(years => years.map(year => ({ label: year, value: year })))
    );
    this.months$ = this.selectMonths();
    this.monthFilterOptions$ = this.months$.pipe(
      map(months => months.map(month => ({ label: month.name, value: month.value })))
    );
    this.checkboxes$ = this.selectCheckboxes();
    this.disableAll$ = this.selectDisableAll();
    this.reportTypes = this.selectReportTypes();
    this.reportTypeOptions = this.reportTypes.map(type => ({ label: type.name, value: type.value }));
  }

  public ngOnInit(): void {
    this.alertService.destroyAlertSnackbar();
    this.subscription.add(this.subscribeSelectedCountry());
    this.subscription.add(this.subscribeMetricDates());
    this.subscription.add(this.subscribeInitialYearFilter());
    this.subscription.add(this.subscribeYearFilter());
    this.subscription.add(this.subscribeInitialMonthFilter());
    this.subscription.add(this.subscribeMonthFilter());
    this.subscription.add(this.subscribeCheckboxes());
    this.subscription.add(this.subscribeLoadAvailability());
    this.subscription.add(this.subscribeSetOptions());
    this.store.dispatch(new ResetReportsDataAction());
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public subscribeSelectedCountry(): Subscription {
    return this.store.pipe(
      select(selectCountrySelected)
    ).subscribe(country => {
      this.country = country;
      this.store.dispatch(new ResetReportsDataAction());
    });
  }

  private subscribeMetricDates(): Subscription {
    return this.store.pipe(
      select(selectMainFilterSelectedAccount),
      distinctUntilChanged(isEqual)
    ).subscribe(account => {
      if (!!account) {
        this.store.dispatch(new LoadReportsDatesAction({ account: account._id }));
      }
    });
  }

  public onChangeYear(event: number): void {

    if (event !== null && event !== undefined) {
      this.store.dispatch(new SetReportsYearFilterAction(event));
      this.store.pipe(
        select(selectReportsMonths),
        take(1)
      ).subscribe(months => {
        this.store.dispatch(new SetReportsMonthFilterAction(months[months.length - 1].value));
      });
    }
  }

  public onChangeMonth(event: number): void {
    if (event !== null && event !== undefined) {
      this.store.dispatch(new SetReportsMonthFilterAction(event));
    }
  }

  public onSelectAllToggle(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.allCheckbox.value = input.checked;
    this.onSelectAll();
  }

  public onSelectAll(): void {
    this.store.pipe(
      select(selectReportsAvailability),
      filter(availability => availability !== undefined),
      take(1),
      map(availability => this.checkAvailability(availability))
    ).subscribe(availability => {
      const allCheckbox = this.allCheckbox.value,
        checkboxes = { ...this.checkboxes };

      Object.keys(checkboxes).forEach(key => {
        if (!!availability.checkboxes[key]) {
          checkboxes[key] = allCheckbox;
        }
      });

      this.store.dispatch(new SetReportsCheckboxOptionsAction({ allCheckbox, checkboxes }));
    });
  }

  public onSelectAny(): void {
    this.store.pipe(
      select(selectReportsAvailability),
      filter(availability => availability !== undefined),
      take(1),
      map(availability => this.checkAvailability(availability))
    ).subscribe(availability => {
      const checkboxes = { ...this.checkboxes },
        allCheckbox = Object.entries(checkboxes)
          .filter(([key]) => !!availability.checkboxes[key])
          .every(([key, value]) => !!value);

      this.store.dispatch(new SetReportsCheckboxOptionsAction({ allCheckbox, checkboxes }));
    });
  }

  public onSelectAnyToggle(event: Event, checkboxKey: string): void {
    const input = event.target as HTMLInputElement;
    this.checkboxes[checkboxKey] = input.checked;
    this.onSelectAny();
  }

  public onShowCheckBoxes(): void {
    this.showCheckboxes = !this.showCheckboxes;
  }

  public onGenerateReport(): void {
    combineLatest([
      this.store.pipe(
        select(selectMainFilterSelectedAccount),
        take(1),
        tap(account => {
          if (!account) {
            return this._toasterService.launchAlertError('Please select an Account Name', Actions.error);
          }
        })
      ),
      this.store.pipe(
        select(selectReportsDate),
        take(1),
        tap(date => {
          if (!date) {
            return this._toasterService.launchAlertError('Please select valid date', Actions.error);
          }
        })),
      this.store.pipe(select(selectReportsOptions)),
      this.store.pipe(select(selectReportsAvailability))
    ])
      .pipe(
        filter(([account, date]) => !!account && !!date),
        map(([account, date, options, availability]) => {
          const checkboxes = {};
          Object.entries(ApiTypeKeys).forEach(([value, key]) => {
            if (availability !== undefined) {
              const checkbox = availability.find(checkbox => checkbox.type === key);
              checkboxes[value] = checkbox.available;
            }
          });
          const type = Object.keys(options.checkboxes)
            .filter(key => {
              const checkKey = key as keyof ReportsCheckboxes;
              if (checkboxes[key] === true) {
                return !!options.checkboxes[checkKey];
              } else {
                return;
              }
            })
            .map(key => {
              const checkKey = key as keyof ReportsCheckboxes;
              return ApiTypeKeys[checkKey];
            })
            .join(';');

          return { account: account._id || '', date, type, country: this.country };
        }),
        take(1)
      ).subscribe(payload => {
        if ((!Object.values(this.checkboxes).some(checkbox => !!checkbox)) || isNullOrUndefined(payload)
          || payload.type.length <= 0) {
          return this._toasterService.launchAlertError('Please select a component', Actions.error);
        } else {
          this.store.dispatch(new ReportsDownloadAction(payload));
        }
      });
  }

  private selectYears(): Observable<number[]> {
    return this.store.pipe(
      select(selectReportsYears)
    );
  }

  private selectMonths(): Observable<Month[]> {
    return this.store.pipe(
      select(selectReportsMonths)
    );
  }

  private selectCheckboxes(): Observable<Checkbox[]> {
    return combineLatest([
      this.store.pipe(select(selectReportsAvailability)),
      this.store.pipe(select(selectMainPageNotes))
    ]).pipe(
      map(([availability, notes]) => ({ availability: this.checkAvailability(availability), notes })),
      map(({ availability, notes }) => Object.keys(availability.checkboxes).map(key => (
        {
          key,
          ...CHECKBOX_INFO[key],
          tooltip: notes.reports[ApiTypeKeys[key]],
          disabled: !availability.checkboxes[key]
        })
      ))
    );
  }

  private selectDisableAll(): Observable<boolean> {
    return this.store.pipe(
      select(selectReportsAvailability),
      map(availability => this.checkAvailability(availability)),
      map(availability => Object.values(availability.checkboxes).every(checkbox => !checkbox))
    );
  }

  private subscribeCheckboxes(): Subscription {
    return this.store.pipe(
      select(selectReportsOptions),
      withLatestFrom(
        combineLatest([
          this.store.pipe(select(selectReportsVisited)),
          this.store.pipe(select(selectAuthUserReports)),
          this.store.pipe(select(selectReportsAvailability)),
          this.store.pipe(select(selectMainPageNotes))]
        )
      )
    ).subscribe(([options, [visited, defaults, availableReports, notes]]) => {
      const reportsAvailable = this.checkAvailability(availableReports);

      if (visited && !!options) {
        const allCheckboxValue = !isNullOrUndefined(reportsAvailable)
          && !isNullOrUndefined(availableReports)
          && availableReports.length > 0 ?
          Object.entries(options.checkboxes)
            .filter(([key]) => !!reportsAvailable.checkboxes[key])
            .every(([key, value]) => !!value) : false;
        this.allCheckbox = {
          value: allCheckboxValue ? allCheckboxValue :
            Object.values(options.checkboxes).length === 7
            && !isNullOrUndefined(availableReports)
            && availableReports.length > 0
            && Object.values(options.checkboxes).every(setting => !!setting),
          tooltip: notes.reports['all']
        };
        this.checkboxes = options.checkboxes;
      }

      if (!visited && !!defaults) {
        const allCheckboxValue = !isNullOrUndefined(reportsAvailable)
          && !isNullOrUndefined(availableReports)
          && availableReports.length > 0 ?
          Object.entries(defaults)
            .filter(([key]) => !!reportsAvailable.checkboxes[key])
            .every(([key, value]) => !!value) : false;
        this.allCheckbox = {
          value: allCheckboxValue ? allCheckboxValue :
            Object.values(defaults).length === 7
            && !isNullOrUndefined(availableReports)
            && availableReports.length > 0
            && Object.values(defaults).every(Boolean),
          tooltip: notes.reports['all']
        };
        this.checkboxes = { ...defaults };

        this.store.dispatch(new SetReportsVisitAction());
      }
    });
  }

  private subscribeInitialYearFilter(): Subscription {
    return combineLatest([
      this.store.pipe(select(selectReportsYears)),
      this.store.pipe(select(selectReportsYearFilter))
    ]
    ).pipe(
      filter(([years]) => !!years.length),
      map(([years, year]) => year !== undefined ? year : years[0])
    ).subscribe(year => {
      this.store.dispatch(new SetReportsYearFilterAction(year));
    });
  }

  private subscribeYearFilter(): Subscription {
    return this.store.pipe(
      select(selectReportsYearFilter)
    ).subscribe(year => {
      this.year = year !== undefined ? year : undefined;
    });
  }

  private subscribeInitialMonthFilter(): Subscription {
    return combineLatest([
      this.store.pipe(select(selectReportsMonths)),
      this.store.pipe(select(selectReportsMonthFilter))]
    ).pipe(
      filter(([months, month]) => !!months.length && month === undefined),
      map(([months, month]) => month !== undefined ? month : months[months.length - 1].value)
    ).subscribe(month => {
      this.store.dispatch(new SetReportsMonthFilterAction(month));
    });
  }

  private subscribeMonthFilter(): Subscription {
    return this.store.pipe(
      select(selectReportsMonthFilter)
    ).subscribe(month => {
      this.month = month !== undefined ? month : undefined;
    });
  }

  private subscribeLoadAvailability(): Subscription {
    return combineLatest([
      this.store.pipe(select(selectReportsDate)),
      this.store.pipe(select(selectMainFilterSelectedAccount))]
    ).pipe(
      filter(([date, account]) => date !== undefined && account !== undefined),
      map(([date, account]) => ({ date, account: account._id }))
    ).subscribe(payload => {
      this.store.dispatch(new LoadReportsAvailabilityAction(payload));
    });
  }

  private subscribeSetOptions(): Subscription {
    return this.store.pipe(
      select(selectReportsAvailability),
      filter(availability => availability !== undefined),
      map(availability => this.checkAvailability(availability)),
      map(availability => this.checkSelection(availability))
    ).subscribe(payload => {
      this.store.dispatch(new SetReportsCheckboxOptionsAction(payload));
    });
  }

  private checkAvailability(availability: ReportsAvailability[]): ReportsOptions {
    let checkboxes;

    Object.entries(ApiTypeKeys).forEach(([value, key]) => {
      if (availability !== undefined) {
        const checkbox = availability.find(checkbox => checkbox.type === key);
        if (!!checkbox) {
          checkboxes = Object.assign({}, checkboxes, { [value]: checkbox.available });
        } else {
          checkboxes = Object.assign({}, checkboxes, { [value]: false });
        }
      } else {
        checkboxes = Object.assign({}, checkboxes, { [value]: false });
      }
    });

    return { allCheckbox: !Object.values(checkboxes).includes(false), checkboxes };
  }

  private checkSelection(availability: ReportsOptions): ReportsOptions {
    if (this.checkboxes) {
      Object.keys(availability.checkboxes).forEach(key => {
        availability.checkboxes[key] = this.checkboxes[key];
        if (!this.checkboxes[key] && availability.allCheckbox) {
          availability.allCheckbox = false;
        }
      });
    }
    return availability;
  }

  public onExportCSV(type): void {
    combineLatest([
      this.store.pipe(select(selectMainFilterSelectedClient),
        take(1),
        tap(client => {
          if (!client) {
            return this._toasterService.launchAlertError('Please select a Client Name', Actions.error);
          }
        })
      ),
      this.store.pipe(select(selectMainFilterSelectedAccount),
        take(1),
        tap(account => {
          if (!account && type === 'investment-summary') {
            return this._toasterService.launchAlertError('Please select an Account Name', Actions.error);
          }
        })
      )
    ])
      .subscribe(([client, account]) => {
        if ((!isNullOrUndefined(client) && (type !== 'investment-summary')) ||
          (!isNullOrUndefined(account) && (type === 'investment-summary'))) {
          const id = (type === 'investment-summary') ? account._id : client._id,
            payload = {
              type,
              from: formatDateFromAndTo(this.from),
              to: formatDateFromAndTo(this.to),
              id,
              country: this.country,
              userDate: moment().format('DD/MM/YYYY h:mm:ss a ') + Intl.DateTimeFormat().resolvedOptions().timeZone
            };

          this.store.dispatch(new ReportsExcelDownloadAction(payload));
        }
      });

  }

  private selectReportTypes() {
    this.reportType = {
      name: 'Monthly Report',
      value: 'monthly-report'
    };
    this.reportType.value = 'monthly-report'

    return [
      {
        name: 'Monthly Report',
        value: 'monthly-report'
      },
      {
        name: 'Excel Reports',
        value: 'excel-reports'
      }
    ];
  }

  public onChangeFilter(event) {
    if (event) {
      (event === 'excel-reports') ? this.showExcel = true : this.showExcel = false;
    }
  }
}
